/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	useCallback,
	useEffect,
	useRef,
	useState,
	type MutableRefObject,
	type ReactNode,
	Suspense,
} from 'react';

import { cssMap, cx, jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { defineMessages, useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import SlideIn from '@atlaskit/motion/slide-in';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import {
	SPLIT_LAYOUT_ADJACENT_MIN_WIDTH,
	SPLIT_LAYOUT_INITIAL_WIDTH,
	SPLIT_LAYOUT_MIN_PANEL_WIDTH,
	SPLIT_LAYOUT_MIN_WIDTH,
	SPLIT_LAYOUT_STORAGE_KEY,
} from '../../common/constants/private';
import { SessionRightPanelErrorBoundary } from '../session-right-panel/SessionRightPanelErrorBoundary';
import { SessionRightPanelLazy } from '../session-right-panel/SessionRightPanelLazy';
import { SessionRightPanelSkeleton } from '../session-right-panel/SessionRightPanelSkeleton';

const SPLIT_LAYOUT_RESIZE_HANDLE_WIDTH = 4;

const messages = defineMessages({
	resizeSidePanelAriaLabel: {
		id: 'ai-mate.conversation-assistant.split-layout.resize-side-panel-aria-label',
		defaultMessage: 'Resize side panel',
		description:
			'Aria label for the draggable handle that resizes the side panel of the conversation assistant split layout',
	},
});

const styles = cssMap({
	root: {
		boxSizing: 'border-box',
		flex: '1 1 auto',
		alignSelf: 'stretch',
		height: '100%',
		width: '100%',
		minHeight: '0',
		minWidth: '0',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'stretch',
	},
	leftPane: {
		alignSelf: 'stretch',
		flex: '1 1 0',
		height: '100%',
		minHeight: '0',
		minWidth: '0',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
	},
	rightPanel: {
		boxSizing: 'border-box',
		alignSelf: 'stretch',
		flex: '0 0 auto',
		height: '100%',
		minHeight: '0',
		minWidth: '0',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderLeftColor: token('color.border'),
		overflow: 'hidden',
	},
	resizeHandle: {
		width: '4px',
		alignSelf: 'stretch',
		position: 'relative',
		cursor: 'col-resize',
		border: 'none',
		backgroundColor: 'transparent',
		transition: 'background-color 0.2s ease-in-out',
		'&::after': {
			content: '""',
			position: 'absolute',
			left: '50%',
			top: '0',
			bottom: '0',
			width: '2px',
			backgroundColor: 'transparent',
			transform: 'translateX(-50%)',
			transition: 'background-color 0.2s ease-in-out',
		},
		'&:hover::after': {
			backgroundColor: token('color.background.brand.bold'),
		},
	},
	resizeHandleDragging: {
		'&::after': {
			backgroundColor: token('color.background.brand.bold'),
		},
	},
});

export type SplitLayoutProps = {
	/**
	 * Initial width (px) of the **right** pane. Defaults make the right pane
	 * narrower than the chat (which fills the remaining horizontal space).
	 * Ignored if a value has been previously persisted under `storageKey`.
	 */
	initialWidth?: number;
	/** Minimum width (px) of the left (chat) panel — the chat is always at least this wide. */
	minWidth?: number;
	/** Minimum width (px) of the right pane. */
	adjacentMinWidth?: number;
	/**
	 * `localStorage` key for persisting the right panel width across reloads.
	 * Defaults to {@link SPLIT_LAYOUT_STORAGE_KEY}; pass `''` (empty string) to
	 * opt out of persistence.
	 */
	storageKey?: string;
	/** The chat content rendered as the left (flex-grow) pane. */
	children: ReactNode;
	/**
	 * Whether the right panel is shown. When toggled the right panel animates in or out.
	 * @default true
	 */
	isRightPanelVisible?: boolean;
	/**
	 * Called when the layout's available width drops below
	 * {@link SPLIT_LAYOUT_MIN_PANEL_WIDTH} while the right panel is visible.
	 */
	onAutoCollapse?: () => void;
	/** Callback mailbox invoked after the right panel finishes animating out. */
	rightPanelExitHandlerRef?: MutableRefObject<(() => void) | null>;
};

const isBrowser = typeof window !== 'undefined';

const readPersistedWidth = (storageKey: string | undefined): number | null => {
	if (!storageKey || !isBrowser) {
		return null;
	}
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		const raw = window.localStorage.getItem(storageKey);
		if (raw == null) {
			return null;
		}
		const parsed = Number.parseFloat(raw);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	} catch {
		return null;
	}
};

const writePersistedWidth = (storageKey: string | undefined, width: number): void => {
	if (!storageKey || !isBrowser) {
		return;
	}
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		window.localStorage.setItem(storageKey, String(Math.round(width)));
	} catch {
		// Best-effort persistence; ignore quota / disabled-storage errors.
	}
};

/**
 * Renders a horizontal split layout:
 *   [ chat children (flex-grow left pane) ] [ resize handle ] [ right panel (sized) ]
 *
 * - The chat (left) uses `flex: 1` so it always absorbs surplus horizontal
 *   space and is the visually dominant pane.
 * - The right panel has an explicit pixel width that the user can drag.
 *
 * The drag math is a mirror of `useResizablePanel`: `rightWidth = containerRight
 * - clientX`, clamped so the chat keeps at least `minWidth`, the right panel keeps at least
 * `adjacentMinWidth`, and the right panel never becomes wider than the chat.
 */
export const SplitLayout = ({
	initialWidth = SPLIT_LAYOUT_INITIAL_WIDTH,
	minWidth = SPLIT_LAYOUT_MIN_WIDTH,
	adjacentMinWidth = SPLIT_LAYOUT_ADJACENT_MIN_WIDTH,
	storageKey = SPLIT_LAYOUT_STORAGE_KEY,
	children,
	isRightPanelVisible = true,
	onAutoCollapse,
	rightPanelExitHandlerRef,
}: SplitLayoutProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const containerRef = useRef<HTMLDivElement>(null);
	// Mirror the callback to a ref so the resize observer below doesn't need to re-subscribe every time the host passes a new callback identity.
	const onAutoCollapseRef = useRef(onAutoCollapse);

	// Lazy initializer so we only touch `localStorage` once on mount. The persisted value (if any) wins over the explicit `initialWidth` prop.
	const [rightWidth, setRightWidth] = useState<number>(() => {
		const persisted = readPersistedWidth(storageKey);
		const seed = persisted ?? initialWidth;
		return Math.max(seed, adjacentMinWidth);
	});
	const [isDragging, setIsDragging] = useState(false);
	const [isRightPanelMounted, setIsRightPanelMounted] = useState(isRightPanelVisible);

	// Latest width is mirrored to a ref so the `mouseup` handler can persist without being recreated (and re-binding listeners) on every drag tick.
	const rightWidthRef = useRef(rightWidth);

	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (!isDragging || !containerRef.current) {
				return;
			}
			const rect = containerRef.current.getBoundingClientRect();
			const proposed = rect.right - e.clientX;
			const maxRight = Math.max(
				adjacentMinWidth,
				Math.min(rect.width - minWidth, (rect.width - SPLIT_LAYOUT_RESIZE_HANDLE_WIDTH) / 2),
			);
			setRightWidth(Math.max(adjacentMinWidth, Math.min(maxRight, proposed)));
		},
		[isDragging, minWidth, adjacentMinWidth],
	);

	const handleMouseUp = useCallback(() => {
		setIsDragging(false);
		// Persist the resting width once per drag (not on every mousemove tick).
		writePersistedWidth(storageKey, rightWidthRef.current);
	}, [storageKey]);

	useEffect(() => {
		if (isRightPanelVisible) {
			setIsRightPanelMounted(true);
		}
	}, [isRightPanelVisible]);

	useEffect(() => {
		rightWidthRef.current = rightWidth;
	}, [rightWidth]);

	useEffect(() => {
		onAutoCollapseRef.current = onAutoCollapse;
	}, [onAutoCollapse]);

	// When the panel the chat lives in is resized narrower than the split-layout minimum, auto-collapse the right panel
	useEffect(() => {
		if (!isRightPanelVisible || !isBrowser || typeof ResizeObserver === 'undefined') {
			return;
		}
		const container = containerRef.current;
		if (!container) {
			return;
		}
		// Collapse the right panel only when the user actively *narrows* the container below the split
		// minimum — i.e. the width is shrinking. Acting on the shrink direction (rather than any narrow
		// reading) means we ignore the panel growing as it expands on open, so a just-opened split is
		// never collapsed mid-expansion, and a split that simply opened narrow isn't yanked away —
		// only a deliberate narrowing hides it.
		let previousWidth: number | null = null;
		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const { width } = entry.contentRect;
				const isShrinking = previousWidth !== null && width < previousWidth;
				if (isShrinking && width < SPLIT_LAYOUT_MIN_PANEL_WIDTH) {
					onAutoCollapseRef.current?.();
				}
				previousWidth = width;
			}
		});
		observer.observe(container);
		return () => {
			observer.disconnect();
		};
	}, [isRightPanelVisible]);

	useEffect(() => {
		if (!isDragging) {
			return;
		}
		const doc = getDocument();
		if (!doc) {
			return;
		}
		const unbindMouseMove = bind(doc, {
			type: 'mousemove',
			listener: handleMouseMove,
		});
		const unbindMouseUp = bind(doc, {
			type: 'mouseup',
			listener: handleMouseUp,
		});
		return () => {
			unbindMouseMove();
			unbindMouseUp();
		};
	}, [isDragging, handleMouseMove, handleMouseUp]);

	const rightStyle: React.CSSProperties = {
		width: `${rightWidth}px`,
		maxWidth: `calc(50% - ${SPLIT_LAYOUT_RESIZE_HANDLE_WIDTH / 2}px)`,
		pointerEvents: isDragging ? 'none' : undefined,
	};

	return (
		<div data-testid="conversation-assistant-split-layout" ref={containerRef} css={styles.root}>
			<div data-testid="conversation-assistant-split-layout-left" css={styles.leftPane}>
				{children}
			</div>
			{isRightPanelMounted && (
				<Pressable
					xcss={cx(styles.resizeHandle, isDragging && styles.resizeHandleDragging)}
					data-dragging={isDragging}
					testId="conversation-assistant-split-layout-resize-handle"
					onMouseDown={handleMouseDown}
					aria-label={formatMessage(messages.resizeSidePanelAriaLabel)}
				/>
			)}
			<ExitingPersistence appear>
				{isRightPanelVisible && (
					<SlideIn
						enterFrom="right"
						fade="inout"
						duration="medium"
						onFinish={(state) => {
							if (state === 'exiting') {
								setIsRightPanelMounted(false);
								const exitHandler = rightPanelExitHandlerRef?.current;
								if (rightPanelExitHandlerRef) {
									rightPanelExitHandlerRef.current = null;
								}
								exitHandler?.();
							}
						}}
					>
						{({ className, ref }) => (
							<div
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- required to forward the motion animation class from @atlaskit/motion's SlideIn render prop
								className={className}
								ref={ref}
								data-testid="conversation-assistant-split-layout-right"
								css={styles.rightPanel}
								style={rightStyle}
							>
								<SessionRightPanelErrorBoundary>
									<Suspense fallback={<SessionRightPanelSkeleton />}>
										<SessionRightPanelLazy />
									</Suspense>
								</SessionRightPanelErrorBoundary>
							</div>
						)}
					</SlideIn>
				)}
			</ExitingPersistence>
		</div>
	);
};
